import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// QC method-spec VERSIONS — change control on the analysis spec (F7, SPEC-039; BE companion
// SPEC-023). Until F7 the QC spec (measured/calculated parameters + formula + required consumables)
// was edited in place on the analysis definition. F7 gives it the same reviewer→supervisor change
// control the forskrifter / SST-templates have, with full version history.
//
// The DRAFT version is created SERVER-SIDE on the analysis-definition save (when the spec CONTENT
// actually changes — the same change detection that drives specChangeReason): the definition save
// adds a new `draft` AnalysisQcSpecVersion and leaves the EFFECTIVE projection (what result-entry
// reads) untouched. This slice does NOT create versions; it lists them and drives the e-sign:
//
//   GET  /lims/spec-versions?analysisDefinitionId=<id>            → {items:[<version>...]}
//   POST /lims/spec-versions/<vid>/reviewer-approve  {password, reason?} → {item}
//        (draft → reviewer-approved; "reviewer" capability, 4-eyes ≠ version creator)
//   POST /lims/spec-versions/<vid>/supervisor-approve {password, reason?} → {item}
//        (reviewer-approved → effective, prior effective → superseded, content copied to the
//         effective projection; "supervisor" capability, 4-eyes ≠ creator + reviewer signer)
//
// One version is `effective` per definition at a time; result-entry always reads the effective
// projection, so a pending draft never affects live results until it is supervisor-approved.
//
// Persisted via the REAL endpoints (SPEC-021 cutover): password + reason ride at the request-body
// TOP LEVEL on the signed calls. With showLims off this slice is inert (no spec-version UI mounts).

// The version state machine (mirrors the backend SPEC_VERSION_STATES + the forskrift / SST one).
export const SPEC_VERSION_STATES = {
  DRAFT: "draft",
  REVIEWER_APPROVED: "reviewer-approved",
  EFFECTIVE: "effective",
  SUPERSEDED: "superseded",
}

// state shape: { byDefinition: { [analysisDefinitionId]: [<version>...] }, isLoading }
//
// A version is { id, analysisDefinitionId, versionNumber, state, parameters, formula,
//   requiredConsumables, createdBy, createdAt, effectiveAt, supersededAt,
//   reviewerSignature|null, supervisorSignature|null }   // oldest-first
// Shared action type for both approve thunks (each returns one updated version that the reducer
// merges into the grouped state) — one place to maintain, and avoids a duplicated-literal warning.
const UPSERT_ACTION = "limsSpecVersions/upsert"

const initialState = { byDefinition: {}, isLoading: false }

function sortVersions(items) {
  return [...items].sort((a, b) => (a.versionNumber || 0) - (b.versionNumber || 0))
}

// Group a flat version list by analysisDefinitionId (each group oldest-first).
function groupByDefinition(items) {
  const byDef = {}
  ;(items || []).forEach((v) => {
    if (!v || !v.analysisDefinitionId) return
    if (!byDef[v.analysisDefinitionId]) byDef[v.analysisDefinitionId] = []
    byDef[v.analysisDefinitionId].push(v)
  })
  Object.keys(byDef).forEach((k) => {
    byDef[k] = sortVersions(byDef[k])
  })
  return byDef
}

// Merge one updated version into the grouped state (replace same id, else append + re-sort).
function upsertVersion(byDefinition, version) {
  if (!version || !version.analysisDefinitionId) return byDefinition
  const defId = version.analysisDefinitionId
  const existing = byDefinition[defId] || []
  const next = existing.some((v) => v.id === version.id)
    ? existing.map((v) => (v.id === version.id ? version : v))
    : [...existing, version]
  return { ...byDefinition, [defId]: sortVersions(next) }
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsSpecVersions/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsSpecVersions/fetch":
      return { byDefinition: groupByDefinition(action.res), isLoading: false }
    case UPSERT_ACTION:
      return { ...state, byDefinition: upsertVersion(state.byDefinition, action.res) }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// Fetch the spec versions for ONE analysis definition (the analysis-config screen calls this per
// definition it edits). Returns the version list; the reducer groups it by definition id (merging
// over any other definition's already-loaded versions would lose them, so we re-group on the dump —
// a single-definition fetch replaces the whole map; callers fetch the definition they are viewing).
export const fetch = asyncAction("limsSpecVersions/fetch", (_dispatch, analysisDefinitionId) => {
  const qs = analysisDefinitionId ? `?analysisDefinitionId=${encodeURIComponent(analysisDefinitionId)}` : ""
  return limsRequest("GET", `/lims/spec-versions${qs}`).then((res) => res.items)
})

// Reviewer-approves a draft spec version: draft → reviewer-approved. password + reason ride at the
// request-body TOP LEVEL; the server enforces "reviewer" capability + 4-eyes (≠ the version creator).
export const reviewerApprove = asyncAction(UPSERT_ACTION, (_dispatch, versionId, signature) => {
  return limsRequest("POST", `/lims/spec-versions/${versionId}/reviewer-approve`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// Supervisor-approves a reviewer-approved spec version: → effective; the server supersedes the
// prior effective version and copies the new content into the effective projection result-entry
// reads — all in one transaction. "supervisor" capability, 4-eyes (≠ creator AND reviewer signer).
export const supervisorApprove = asyncAction(UPSERT_ACTION, (_dispatch, versionId, signature) => {
  return limsRequest("POST", `/lims/spec-versions/${versionId}/supervisor-approve`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// ---- selectors -------------------------------------------------------------------------------

// Every spec version for an analysis definition (oldest-first), or [].
export const selectVersionsForDefinition = (state, analysisDefinitionId) =>
  (analysisDefinitionId && state.limsSpecVersions.byDefinition[analysisDefinitionId]) || []

// The single EFFECTIVE spec version of a definition (the content result-entry reads), or null.
export const selectEffectiveVersion = (state, analysisDefinitionId) =>
  selectVersionsForDefinition(state, analysisDefinitionId).find(
    (v) => v.state === SPEC_VERSION_STATES.EFFECTIVE
  ) || null

// The pending (draft or reviewer-approved) spec version awaiting approval, or null. There is at
// most one non-terminal version at a time (a content change reuses/replaces the open draft path).
export const selectPendingVersion = (state, analysisDefinitionId) =>
  selectVersionsForDefinition(state, analysisDefinitionId).find(
    (v) => v.state === SPEC_VERSION_STATES.DRAFT || v.state === SPEC_VERSION_STATES.REVIEWER_APPROVED
  ) || null
